import { useMutation } from "@apollo/client";
import moment from "moment";
import { useMemo } from "react";
import {
  GoalArtifactNode,
  GoalState,
  PermissionNode,
} from "types/graphql-schema";

import getArtifactActivitiesQuery from "@apps/artifact-sidebar/graphql/get-artifact-activities-query";
import { buttonTheme } from "@components/button/button";
import InputDatePicker from "@components/input/input-datepicker";

import updateArtifactMutation from "../graphql/update-artifact-mutation";

const GoalDueDate = ({
  artifact,
}: {
  artifact: Pick<GoalArtifactNode, "id" | "startDate" | "dueDate" | "state"> & {
    canUpdate: Pick<PermissionNode, "permission">;
  };
}) => {
  const [updateArtifact] = useMutation(updateArtifactMutation);

  const handleSetDueDate = (date: Date | null) => {
    if (date === null) return;
    const dueDate = date ? moment(date).format("YYYY-MM-DD") : null;
    updateArtifact({
      variables: {
        artifactId: artifact.id,
        additionalFields: { dueDate },
      },
      optimisticResponse: {
        createOrUpdateArtifact: {
          artifact: {
            ...artifact,
            dueDate,
          },
          __typename: "CreateOrUpdateArtifactMutation",
        },
      },
      refetchQueries: [getArtifactActivitiesQuery],
    });
  };

  const isCompleted = useMemo(
    () => artifact.state === GoalState.Closed,
    [artifact.state]
  );

  const isOverdue = useMemo(
    () =>
      !isCompleted &&
      artifact.dueDate &&
      moment(artifact.dueDate).isBefore(moment()),
    [artifact.dueDate, isCompleted]
  );

  return (
    <InputDatePicker
      date={artifact.dueDate}
      onChangeDate={handleSetDueDate}
      isClearable={false}
      minDate={moment(artifact.startDate).toDate()}
      disabled={!artifact.canUpdate.permission}
      placeholderText="Due date"
      buttonProps={{
        theme: isOverdue ? buttonTheme.textRed : buttonTheme.text,
        tooltip: artifact.dueDate
          ? `Due on ${moment(artifact.dueDate).format("dddd, MMMM D, YYYY")}`
          : "Add due date",
      }}
    />
  );
};

export default GoalDueDate;
